import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSongStore } from '../store/useSongStore'
import { SECTION_TYPES } from '../config/songbook'
import { useI18n } from '../i18n'
import type { Song } from '../types/songbook'

const STORAGE_KEY = 'songbook-live-settings'

// Tailwind class -> hex for dynamic opacity
const COLOR_MAP: Record<string, string> = {
  'bg-purple-600': '#9333ea',
  'bg-blue-600': '#2563eb',
  'bg-green-600': '#16a34a',
  'bg-yellow-600': '#ca8a04',
  'bg-orange-600': '#ea580c',
  'bg-red-600': '#dc2626',
  'bg-pink-600': '#db2777',
  'bg-gray-600': '#4b5563',
  'bg-surface-lighter': '#4b5563',
}

type FontSize = 'small' | 'medium' | 'large'
type ScrollState = 'idle' | 'running' | 'paused'

const FONT_CLASSES: Record<FontSize, { label: string; note: string }> = {
  small: { label: 'text-base', note: 'text-sm' },
  medium: { label: 'text-xl', note: 'text-base' },
  large: { label: 'text-3xl', note: 'text-lg' },
}

function loadSettings(): { opacity: number; fontSize: FontSize } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        opacity: typeof parsed.opacity === 'number' ? parsed.opacity : 1,
        fontSize: ['small', 'medium', 'large'].includes(parsed.fontSize) ? parsed.fontSize : 'medium',
      }
    }
  } catch { /* ignore */ }
  return { opacity: 1, fontSize: 'medium' }
}

function saveSettings(opacity: number, fontSize: FontSize) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ opacity, fontSize }))
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r}, ${g}, ${b}`
}

export function PlaylistLivePage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { playlistId } = useParams<{ playlistId: string }>()
  const [searchParams] = useSearchParams()
  const { songs, playlists, fetchSongs, fetchPlaylists } = useSongStore()
  const [currentIndex, setCurrentIndex] = useState(() => {
    const start = parseInt(searchParams.get('start') ?? '0')
    return isNaN(start) ? 0 : start
  })
  const [showSettings, setShowSettings] = useState(false)
  const [settings, setSettings] = useState(loadSettings)
  const { opacity, fontSize } = settings
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Auto-scroll state
  const [scrollState, setScrollState] = useState<ScrollState>('idle')
  const scrollRef = useRef<HTMLElement>(null)
  const rafRef = useRef<number | null>(null)
  const scrollStartRef = useRef<{ time: number; startTop: number; totalPx: number } | null>(null)
  const pausedAtRef = useRef<number>(0)
  const durationRef = useRef<number | null>(null)

  const fullscreenSupported = typeof document !== 'undefined' &&
    (document.documentElement.requestFullscreen != null ||
      // @ts-expect-error vendor prefix
      document.documentElement.webkitRequestFullscreen != null)

  useEffect(() => {
    function onChange() {
      setIsFullscreen(document.fullscreenElement != null)
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      } else {
        const el = document.documentElement
        const req = el.requestFullscreen
          // @ts-expect-error vendor prefix
          ?? el.webkitRequestFullscreen
        if (req) await req.call(el)
      }
    } catch (e) {
      console.warn('fullscreen toggle failed', e)
    }
  }

  function updateOpacity(val: number) {
    const next = { ...settings, opacity: val }
    setSettings(next)
    saveSettings(next.opacity, next.fontSize)
  }

  function updateFontSize(val: FontSize) {
    const next = { ...settings, fontSize: val }
    setSettings(next)
    saveSettings(next.opacity, next.fontSize)
  }

  useEffect(() => {
    void fetchPlaylists()
    void fetchSongs()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll when song changes
  useEffect(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    scrollStartRef.current = null
    pausedAtRef.current = 0
    durationRef.current = null
    setScrollState('idle')
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [currentIndex])

  // rAF scroll loop
  useEffect(() => {
    if (scrollState !== 'running') {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      return
    }

    const el = scrollRef.current
    if (!el || !durationRef.current) return
    const songDuration = durationRef.current

    const totalPx = el.scrollHeight - el.clientHeight
    if (totalPx <= 0) return

    const startTop = pausedAtRef.current
    const pxPerSec = totalPx / songDuration
    const startTime = performance.now()

    function tick(now: number) {
      const container = scrollRef.current
      if (!container) return
      const elapsed = (now - startTime) / 1000
      const top = startTop + elapsed * pxPerSec
      container.scrollTop = top
      pausedAtRef.current = top
      if (top < totalPx) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        container.scrollTop = totalPx
        setScrollState('paused')
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [scrollState])

  const playlist = playlists.find((p) => p.id === playlistId)
  if (!playlist) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-muted">{t('general.loading')}</p>
      </div>
    )
  }

  const orderedSongs = playlist.songIds
    .map((id) => songs.find((s) => s.id === id))
    .filter(Boolean) as Song[]

  if (orderedSongs.length === 0) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-muted">{t('songbook.noPlaylists')}</p>
      </div>
    )
  }

  const song = orderedSongs[Math.min(currentIndex, orderedSongs.length - 1)]
  const isFirst = currentIndex === 0
  const isLast = currentIndex >= orderedSongs.length - 1
  const fontCls = FONT_CLASSES[fontSize]
  const duration = song.duration ?? null

  const sections = song.elements
    .filter((el) => el.type === 'section')
    .map((el) => {
      if (el.type !== 'section') return { label: '', colorHex: '#4b5563', note: '' }
      const cfg = SECTION_TYPES.find((s) => s.id === el.sectionTypeId)
      const label = el.customName || (cfg ? t(cfg.i18nKey as Parameters<typeof t>[0]) : el.sectionTypeId)
      const twColor = cfg?.color ?? 'bg-surface-lighter'
      const colorHex = COLOR_MAP[twColor] ?? '#4b5563'
      return { label, colorHex, note: el.note ?? '' }
    })

  function handleContentTap() {
    if (!duration) return
    if (scrollState === 'idle') return
    setScrollState((s) => s === 'running' ? 'paused' : 'running')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3">
        <button
          className="shrink-0 rounded p-1 text-text-muted hover:text-text"
          onClick={() => navigate(`/playlist/${playlistId}`)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <div className="min-w-0 flex-1 text-center">
          <h1 className="truncate text-lg font-bold text-text">{song.title || t('songbook.untitled')}</h1>
          <p className="text-xs text-text-muted">
            {currentIndex + 1} / {orderedSongs.length} &mdash; {playlist.name}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {fullscreenSupported && (
            <button
              className="rounded p-1 text-text-muted hover:text-text"
              onClick={toggleFullscreen}
              title={isFullscreen ? t('songbook.exitFullscreen') : t('songbook.enterFullscreen')}
            >
              {isFullscreen ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                </svg>
              )}
            </button>
          )}
          <button
            className="rounded p-1 text-text-muted hover:text-text"
            onClick={() => setShowSettings((v) => !v)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      </header>

      {/* Settings panel */}
      {showSettings && (
        <div className="mx-4 mb-3 rounded-lg border border-surface-lighter bg-surface-light p-4">
          <div className="mb-4">
            <label className="mb-2 block text-sm font-semibold text-text">
              {t('songbook.liveOpacity')}
            </label>
            <input
              type="range"
              min="0.2"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => updateOpacity(parseFloat(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="mt-1 flex justify-between text-xs text-text-muted">
              <span>20%</span>
              <span>{Math.round(opacity * 100)}%</span>
              <span>100%</span>
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-text">
              {t('songbook.liveFontSize')}
            </label>
            <div className="flex gap-2">
              {(['small', 'medium', 'large'] as const).map((size) => (
                <button
                  key={size}
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-medium ${
                    fontSize === size
                      ? 'border-primary bg-primary/20 text-primary'
                      : 'border-surface-lighter text-text-muted hover:text-text'
                  }`}
                  onClick={() => updateFontSize(size)}
                >
                  {t(`songbook.fontSize.${size}` as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section list — tappable to pause/resume scroll */}
      <main
        ref={scrollRef}
        className="relative flex-1 overflow-y-auto px-4 pb-32"
        onClick={handleContentTap}
      >
        <div className="space-y-2">
          {sections.map((sec, i) => (
            <div
              key={i}
              className="rounded-lg px-4 py-3"
              style={{ backgroundColor: `rgba(${hexToRgb(sec.colorHex)}, ${opacity})` }}
            >
              <span className={`${fontCls.label} font-semibold text-white`}>{sec.label}</span>
              {sec.note && (
                <p className={`mt-1 ${fontCls.note} italic text-white/70`}>{sec.note}</p>
              )}
            </div>
          ))}
        </div>
        {sections.length === 0 && (
          <p className="py-10 text-center text-text-muted">{t('songbook.noSongs')}</p>
        )}

        {/* START TRACK overlay */}
        {duration && scrollState === 'idle' && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => { e.stopPropagation(); durationRef.current = duration; setScrollState('running') }}
          >
            <button
              className="rounded-2xl border-2 border-white px-10 py-5 text-2xl font-bold uppercase tracking-widest text-white active:scale-95"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              Start Track
            </button>
            {duration && (
              <p className="mt-3 text-sm text-white/50">
                {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')} min
              </p>
            )}
          </div>
        )}

        {/* Paused indicator */}
        {duration && scrollState === 'paused' && (
          <div
            className="pointer-events-none fixed inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center"
          >
            <span className="rounded-full bg-black/60 px-4 py-2 text-sm font-semibold text-white/70">
              II
            </span>
          </div>
        )}
      </main>

      {/* Bottom nav */}
      <div className="fixed inset-x-0 bottom-0 flex items-center justify-center gap-6 bg-surface/90 px-4 py-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          className="flex h-[60px] w-[60px] items-center justify-center rounded-xl border-2 border-white disabled:border-surface-lighter disabled:opacity-30"
          disabled={isFirst}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <button
          className="flex h-[60px] w-[60px] items-center justify-center rounded-xl border-2 border-white disabled:border-surface-lighter disabled:opacity-30"
          disabled={isLast}
          onClick={() => setCurrentIndex((i) => Math.min(orderedSongs.length - 1, i + 1))}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </button>
      </div>
    </div>
  )
}
